import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DescriptionIcon from '@mui/icons-material/Description'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { formatDate, formatDateTimeWithSeconds } from '../../../../app/utils'
import { MRT_ColumnDef, MRT_RowSelectionState } from 'material-react-table'
import { useEffect, useMemo, useState } from 'react'
import { Control, FieldErrors, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { NumericFormat } from 'react-number-format'
import { useLoggedInUser } from '../../../../app/hooks/useLoggedInUser'
import {
  useLazyGetProductDatesQuery,
  useLazyGetProductPricesQuery,
} from '../../../../app/redux-fetch/apiProducts'
import { useGetCurrenciesByCustomerIdQuery } from '../../../../app/redux-fetch/apiOrders'

import CustomAlert from '../../../../components/Alerts/CustomAlert'
import ConditionalRender from '../../../../components/ConditionalRender/ConditionalRender'
import FormDatePickerInput from '../../../../components/Forms/FormDatePickerInput'
import FormInput from '../../../../components/Forms/FormInput'
import FormSelect from '../../../../components/Forms/FormSelect'
import MRTDataGridV2 from '../../../../components/MRTDataGrid/MRTDataGridv2'
import { SectionTitleTypography } from '../../../../components/Typographies/styles'
import { IProductPrice } from '../../types'
import CurrencyModal from './CurrencyModal'
import DocumentModal from './DocumentModal'
import PriceModal from './PriceModal'
import { messages } from './messages'

interface PriceRow {
  id: number
  formIndex: number
  currency: string
  currencyLocalisedSymbol: string
  price: number
  validFrom: string
  validTo: string
  daysInUse: number
  currencyId: number
  description: string
}

interface PriceDetailsProps {
  control?: Control<any>
  errors?: FieldErrors
  customerId?: number
  productId?: number
  isViewMode?: boolean
}

const PriceDetails = ({ customerId = -1, isViewMode = false }: PriceDetailsProps) => {
  const { formatMessage } = useIntl()

  const {
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext()

  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false)
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState<PriceRow | null>(null)
  const [selectedPriceForEdit, setSelectedPriceForEdit] = useState<PriceRow | null>(null)
  const [isDocModalOpen, setIsDocModalOpen] = useState(false)
  const [currentDocumentRow, setCurrentDocumentRow] = useState<PriceRow | null>(null)

  const [documentType, setDocumentType] = useState<'PRICE' | 'CURRENCY'>('PRICE')

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | undefined>(undefined)
  const [filteredPriceRows] = useState<PriceRow[]>([])
  const [updatedAt, setUpdatedAt] = useState<string | undefined>(undefined)
  const [priceSetMode, setPriceSetMode] = useState<boolean>(true)

  const [originalPrices, setOriginalPrices] = useState<IProductPrice[]>([])
  const [isPriceModified, setIsPriceModified] = useState<boolean>(false)

  const productId = getValues('id') || -1

  const { data: currencies = [] } = useGetCurrenciesByCustomerIdQuery(
    {
      customerId: customerId,
      hideDisabled: true,
    },
    { skip: isViewMode },
  )

  const [fetchPrices, { data: priceSets }] = useLazyGetProductPricesQuery()
  const [fetchDates, { data: priceDates = [], isFetching: priceDatesLoading }] =
    useLazyGetProductDatesQuery()

  const findPriceIndex = (row: PriceRow): number => {
    if (row.formIndex !== undefined) {
      return row.formIndex
    }
    const formPrices: IProductPrice[] = getValues('prices') || []
    return formPrices.findIndex((p) => {
      const result =
        p.currencyId === row.currencyId &&
        p.price === row.price &&
        p.priceValidFrom === row.validFrom &&
        (p.priceValidTo === row.validTo ||
          ((row.validTo === '' || row.validTo === '∞' || row.validTo === null) &&
            (p.priceValidTo === null || p.priceValidTo === '∞')))
      return result
    })
  }

  const productData = watch()
  const { tenantId } = useLoggedInUser()
  const mapProductPricesToRows = (prices?: IProductPrice[]): PriceRow[] => {
    if (!prices || prices.length === 0) return []

    return prices.map((price, index) => {
      const nested = (price as any).currency as { name?: string; localisedSymbol?: string } | undefined
      const lookup = currencies.find((c) => c.id === price.currencyId)
      return {
        id: price.id || index + 1,
        formIndex: index,
        currencyId: price.currencyId,
        currency: nested?.name ?? lookup?.name ?? `Currency (${price.currencyId})`,
        currencyLocalisedSymbol: nested?.localisedSymbol ?? lookup?.localisedSymbol ?? '',
        price: price.price || 0,
        validFrom: price.priceValidFrom,
        validTo: price.priceValidTo || '',
        daysInUse: calculateDaysInUse(price.priceValidFrom),
        description: price.name || '',
      }
    })
  }

  const calculateDaysInUse = (validFromDate?: string): number => {
    if (!validFromDate) return 0

    const startDate = new Date(validFromDate)
    const today = new Date()
    const diffTime = Math.abs(today.getTime() - startDate.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays
  }

  const priceRows = useMemo(
    () => mapProductPricesToRows(productData.prices),
    [productData.prices, currencies]
  )
  const getActivePrices = (prices: PriceRow[]): PriceRow[] => {
    const currencyMap = new Map<number, PriceRow>()

    prices.forEach((price) => {
      if (
        !currencyMap.has(price.currencyId) ||
        new Date(price.validFrom) > new Date(currencyMap.get(price.currencyId)?.validFrom || '')
      ) {
        currencyMap.set(price.currencyId, price)
      }
    })

    return Array.from(currencyMap.values())
  }

  const activePrices = useMemo(() => {
    return getActivePrices(priceRows)
  }, [priceRows])

  const handleAddNewPrice = (priceData: any) => {
    const currentPrices: IProductPrice[] = getValues('prices') || []
    const newPrice: IProductPrice = {
      productId: productId ?? 0,

      currencyId: priceData.currencyId,
      price: priceData.price ?? 0,
      name: priceData.name ?? '',
      priceValidFrom: priceData.priceValidFrom
        ? formatDate(priceData.priceValidFrom)
        : formatDate(new Date()),
      priceValidTo:
        priceData.priceValidTo === '∞'
          ? '∞'
          : priceData.priceValidTo
            ? formatDate(priceData.priceValidTo)
            : null,
      updatedByUserId: 0,
      documents: [],
    }

    const updatedPrices = [...currentPrices, newPrice]
    setValue('prices', updatedPrices)

    setIsPriceModified(true)

    console.log('Added new price at index:', updatedPrices.length - 1)
  }

  const handleOpenDocuments = (row: PriceRow, type: 'PRICE' | 'CURRENCY') => {
    setCurrentDocumentRow(row)
    setDocumentType(type)
    setIsDocModalOpen(true)
  }

  const handleDeleteCurrency = (row: PriceRow) => {
    const currentPrices: IProductPrice[] = getValues().prices || []

    const updatedPrices = currentPrices.filter((price) => price.currencyId !== row.currencyId)

    setValue('prices', updatedPrices)
    setIsPriceModified(true)

    if (selectedCurrencyId === row.currencyId) {
      setSelectedCurrencyId(undefined)
      setRowSelection({})
    }

    if (activePrices.length > 1) {
      const remainingCurrencies = activePrices.filter(
        (price) => price.currencyId !== row.currencyId
      )
      if (remainingCurrencies.length > 0) {
        const nextCurrencyId = remainingCurrencies[0].id
        setSelectedCurrencyId(nextCurrencyId)
        setRowSelection({ [nextCurrencyId]: true })
      }
    }
  }

  const currencyOptions = useMemo(() => {
    const filteredCurrencies = currencies.filter(
      (c) => !activePrices.some((p) => p.currencyId === c.id)
    )
    return filteredCurrencies.map((currency) => ({
      label: `${currency.iso3 || ''} - ${currency.localisedSymbol || ''}`,
      value: currency.id,
    }))
  }, [currencies, activePrices])

  const handleDeletePrice = (row: PriceRow) => {
    const currentPrices: IProductPrice[] = getValues().prices || []
    const comparePrice = (price: IProductPrice, row: IProductPrice) => {
      return (
        price.currencyId === row.currencyId &&
        price.price === row.price &&
        price.priceValidFrom === row.validFrom &&
        (price.priceValidTo || '') === (row.validTo || '')
      )
    }

    const updatedPrices = currentPrices.filter(
      (price) => !comparePrice(price, row as unknown as IProductPrice)
    )

    setValue('prices', [...updatedPrices])
    setIsPriceModified(true)
  }
  const currencyColumns: MRT_ColumnDef<PriceRow>[] = [
    { accessorKey: 'currency', header: formatMessage(messages.currency) },
    {
      accessorKey: 'price',
      header: formatMessage(messages.activePrice),
      Cell: ({ cell }) => (
        <NumericFormat
          value={cell.getValue<number>()}
          displayType='text'
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      ),
    },
    {
      accessorKey: 'validFrom',
      header: formatMessage(messages.validFrom),
      Cell: ({ cell }) => {
        const date = cell.getValue<Date>()
        return <Typography>{date ? formatDate(date) : ''}</Typography>
      },
    },
    {
      accessorKey: 'daysInUse',
      header: formatMessage(messages.daysInUse),
      Cell: ({ cell }) => (
        <Typography sx={{ textAlign: 'center' }}>{cell.getValue<number>()}</Typography>
      ),
      muiTableHeadCellProps: {
        align: 'center',
      },
      muiTableBodyCellProps: {
        align: 'center',
      },
    },
    {
      accessorKey: 'actions',
      header: '',
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => {
        if (!isViewMode) {
          return (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Tooltip title={formatMessage(messages.delete)}>
                <IconButton
                  color='error'
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteCurrency(row.original)
                  }}
                  size='small'
                  disabled={!priceSetMode}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )
        }
      },
    },
  ]

  const priceColumns: MRT_ColumnDef<PriceRow>[] = [
    {
      accessorKey: 'price',
      header: formatMessage(messages.activePrice),
      Edit: ({ row }) => {
        if (isViewMode) {
          const currencySymbol = row.original.currencyLocalisedSymbol || ''

          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography>
                {row.original.price?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) ?? ''}{' '}
                {currencySymbol}
              </Typography>
            </Box>
          )
        }

        const formIndex = findPriceIndex(row.original)
        if (formIndex === -1) {
          console.warn('Could not find price in form data', row.original)
          return null
        }
        const fieldName = `prices.${formIndex}.price`

        const currencySymbol = row.original.currencyLocalisedSymbol || ''

        return (
          <Box sx={{ position: 'relative', minHeight: '40px' }}>
            <FormInput
              key={fieldName}
              name={fieldName}
              type='number'
              numberFormatProps={{
                decimalScale: 2,
                thousandSeparator: true,
                allowNegative: false,
              }}
              label={''}
              defaultValue={0}
              unit={currencySymbol}
              maxValue={9999999999.99}
              sx={{
                mb: 0,
                '& .MuiFormHelperText-root': {
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  margin: 0,
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                  zIndex: 1,
                },
              }}
            />
          </Box>

        )
      },
      Cell: ({ cell }) => {
        const currencySymbol = cell.row.original.currencyLocalisedSymbol || ''

        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography>
              {cell.getValue<number>()?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) ?? ''}{' '}
              {currencySymbol}
            </Typography>
          </Box>
        )
      },
    },
    {
      accessorKey: 'validFrom',
      header: formatMessage(messages.validFrom),
      enableColumnFilter: true,
      filterFn: 'dateRange',
      filterVariant: 'date-range',
      Cell: ({ cell }) => {
        const date = cell.getValue<Date>()
        return <Typography>{date ? formatDate(date) : ''}</Typography>
      },
      Edit: ({ row }) => {
        if (isViewMode) {
          return (
            <Typography>
              {row.original.validFrom ? formatDate(row.original.validFrom) : ''}
            </Typography>
          )
        }

        const formIndex = findPriceIndex(row.original)

        if (formIndex === -1) {
          console.warn('Could not find price in form data for validFrom field', row.original)
          return null
        }

        const fieldName = `prices.${formIndex}.priceValidFrom`

        return (
          <FormDatePickerInput
            name={fieldName}
            format='YYYY-MM-DD'
            key={fieldName}
            minDate={'1970/01/01'}
          />
        )
      },
    },
    {
      accessorKey: 'validTo',
      header: formatMessage(messages.validTo),
      enableColumnFilter: true,
      filterFn: 'dateRange',
      filterVariant: 'date-range',
      Edit: ({ row }) => {
        if (isViewMode) {
          return (
            <Typography>
              {row.original.validTo === '∞'
                ? '∞'
                : formatDate(row.original.validTo)}
            </Typography>
          )
        }

        const formIndex = findPriceIndex(row.original)

        if (formIndex === -1) {
          console.warn('Could not find price in form data for validTo field', row.original)
          return null
        }

        const fieldName = `prices.${formIndex}.priceValidTo`

        return (
          <FormDatePickerInput
            name={fieldName}
            format='YYYY-MM-DD'
            allowInfinity={true}
            disablePast={false}
            minDate={'1970/01/01'}
            key={fieldName}
          />
        )
      },
      Cell: ({ cell }) => {
        const value = cell.getValue<string>()
        return (
          <Typography>
            {value === '∞' ? '∞' : value ? formatDate(value) : ''}
          </Typography>
        )
      },
    },
    {
      accessorKey: 'daysInUse',
      header: formatMessage(messages.daysInUse),
      enableEditing: false,
      muiTableHeadCellProps: {
        align: 'center',
      },
      muiTableBodyCellProps: {
        align: 'center',
      },
    },
    {
      accessorKey: 'description',
      header: formatMessage(messages.description),
      Edit: ({ row }) => {
        if (isViewMode) {
          return <Typography>{row.original.description || ''}</Typography>
        }

        const formIndex = findPriceIndex(row.original)

        if (formIndex === -1) {
          console.warn('Could not find price in form data for description field', row.original)
          return null
        }

        const fieldName = `prices.${formIndex}.name`

        return (
          <FormInput
            key={fieldName}
            name={fieldName}
            label=''
            placeholder={formatMessage(messages.pleaseEnter)}
            fullWidth
          />
        )
      },
    },
  ]

  const updateSelectedCurrencyFromRowSelection = (newRowSelection: MRT_RowSelectionState) => {
    const selectedRowCurrencyId = Object.keys(newRowSelection).find((id) => newRowSelection[id])
    console.log('Selected currency ID:', selectedRowCurrencyId)

    if (selectedRowCurrencyId && +selectedRowCurrencyId > 0) {
      const selectedCurrency = activePrices.find(
        (price) => price.id === Number(selectedRowCurrencyId)
      )
      if (selectedCurrency) {
        setSelectedCurrency(selectedCurrency)
        setSelectedCurrencyId(selectedCurrency.currencyId)
        if (selectedCurrency.price !== 0) {
          fetchDates({
            tenantId,
            customerId,
            productId: productId ?? -1,
            currencyId: selectedCurrency.currencyId ?? -1,
          })

          setUpdatedAt(undefined)
          setValue('priceSet', '')
        } else {
          handleSetToEditMode(selectedCurrency.currencyId)
        }
      }
    } else {
      setSelectedCurrencyId(undefined)
    }
  }

  const convertPriceDatesToLabelValue = (
    dates: string[]
  ): { label: string; value: string | number; active: boolean; id: number }[] => {
    if (!dates || dates.length === 0) return []

    return dates.map((dateString, index) => {
      const formattedDate = formatDateTimeWithSeconds(new Date(dateString))

      const isActive = index === dates.length - 1

      return {
        label: formattedDate,
        value: dateString,
        active: isActive,
        id: index + 1,
      }
    })
  }

  const priceDateOptions = useMemo(() => {
    return convertPriceDatesToLabelValue(priceDates)
  }, [priceDates])

  useEffect(() => {
    if (
      priceDateOptions &&
      priceDateOptions.length > 0 &&
      !updatedAt &&
      selectedCurrencyId &&
      !priceDatesLoading
    ) {
      const latestOption = priceDateOptions.find((option) => option.active)

      if (latestOption) {
        console.log('Found latest price set option:', latestOption.label)

        const latestDate = latestOption.value as string
        setUpdatedAt(latestDate)
        setValue('priceSet', latestDate)

        const params = {
          tenantId,
          customerId,
          productId: productId ?? -1,
          currencyId: selectedCurrencyId,
          updatedAt: latestDate,
        }

        fetchPrices(params)
      } else {
        console.log('No active price set option found')
      }
    }
  }, [
    priceDateOptions,
    selectedCurrencyId,
    updatedAt,
    setValue,
    fetchPrices,
    tenantId,
    customerId,
    productId,
    priceDatesLoading,
  ])

  useEffect(() => {
    updateSelectedCurrencyFromRowSelection(rowSelection)
  }, [rowSelection, activePrices])

  useEffect(() => {
    if (selectedCurrencyId && updatedAt) {
      console.log('Both currency and date selected, fetching prices...')

      const params = {
        tenantId,
        customerId,
        productId: productId ?? -1,
        currencyId: selectedCurrencyId,
        updatedAt: updatedAt,
      }

      fetchPrices(params)
    }
  }, [selectedCurrencyId, updatedAt, tenantId, customerId, productId, fetchPrices])

  useEffect(() => {
    if (activePrices.length > 0 && Object.keys(rowSelection).length === 0) {
      console.log('Auto-selecting first currency')

      const initialRow = activePrices[0]
      const initialRowId = initialRow?.id ?? 1

      setRowSelection({ [initialRowId]: true })
    }
  }, [activePrices, rowSelection])

  useEffect(() => {
    if (!priceSetMode && originalPrices.length > 0) {
      const currentPrices = getValues().prices || []

      if (JSON.stringify(currentPrices) !== JSON.stringify(originalPrices)) {
        setIsPriceModified(true)
      }
    }
  }, [productData.prices, priceSetMode, originalPrices])

  const filteredPriceHistoryRows = useMemo(() => {
    let rows

    if (
      !isPriceModified &&
      updatedAt &&
      priceSets &&
      priceSets.productPrices &&
      priceSets.productPrices.length > 0
    ) {
      rows = mapProductPricesToRows(priceSets.productPrices)
    } else if (filteredPriceRows.length > 0) {
      rows = filteredPriceRows
    } else if (selectedCurrencyId) {
      rows = priceRows.filter((price) => price.currencyId === selectedCurrencyId)
    } else {
      return []
    }

    return [...rows].sort((a, b) => {
      if (a.validFrom === '∞') return 1
      if (b.validFrom === '∞') return -1
      return new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime()
    })
  }, [priceSets, priceRows, filteredPriceRows, selectedCurrencyId, updatedAt])

  const handleAddPriceClick = () => {
    setIsPriceModalOpen(true)
  }

  const handleSetToEditMode = (currencyId?: number | undefined) => {
    const selectedId = currencyId ?? selectedCurrencyId

    setUpdatedAt(undefined)
    setPriceSetMode((prev) => !prev)

    const filteredPrices =
      priceSets?.productPrices?.filter((price) => price.currencyId === selectedId) || []
    const originalPrices = productData.prices.filter((price: any) => price.price !== 0)

    setOriginalPrices([...originalPrices])

    if (filteredPrices.length > 0) {
      const formattedPrices = filteredPrices.map((price) => ({
        ...price,
        priceValidTo: price.priceValidTo || '∞',
      }))

      const pricesWithoutId = [
        ...formattedPrices,
        ...originalPrices.filter((x: any) => x.currencyId !== selectedId),
      ].map((row: any) => ({
        productId: productId || 0,
        currencyId: row.currencyId,
        price: row.price || 0,
        name: row.description || '',
        priceValidFrom: row.priceValidFrom || new Date().toString(),
        priceValidTo: row.priceValidTo === '∞' ? null : row.priceValidTo || null,
        validFrom: row.validFrom,
        validTo: row.validTo,
        updatedByUserId: row.updatedByUserId || 0,
        documents: Array.isArray(row.documents) ? row.documents : [],
      }))
      setValue('prices', pricesWithoutId)
    }
    setIsPriceModified(false)
  }

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      {errors.prices &&
        ((typeof errors.prices.root?.message === 'string' &&
          errors.prices.root.message.includes('one price entry must have no end date')) ||
          (typeof errors.prices?.message === 'string' &&
            errors.prices?.message?.includes('one price entry must have no end date'))) && (
          <CustomAlert
            severity='error'
            title='Price Validation Error'
            message='At least one price entry must have no end date.'
            gridProps={{ xs: 12, sx: { mb: 2 } }}
          />
        )}

      {errors.prices &&
        ((typeof errors.prices.root?.message === 'string' &&
          errors.prices.root.message.includes('gaps between price date ranges')) ||
          (typeof errors.prices?.message === 'string' &&
            errors.prices?.message?.includes('gaps between price date ranges'))) && (
          <CustomAlert
            severity='error'
            title='Price Validation Error'
            message='There cannot be gaps between price date ranges.'
            gridProps={{ xs: 12, sx: { mb: 2 } }}
          />
        )}

      {errors.prices &&
        ((typeof errors.prices.root?.message === 'string' &&
          errors.prices.root.message.includes('active price for the current date')) ||
          (typeof errors.prices?.message === 'string' &&
            errors.prices?.message?.includes('active price for the current date'))) && (
          <CustomAlert
            severity='error'
            title='Price Validation Error'
            message='There must be an active price for the current date.'
            gridProps={{ xs: 12, sx: { mb: 2 } }}
          />
        )}

      {errors.prices &&
        ((typeof errors.prices.root?.message === 'string' &&
          errors.prices.root.message.includes('multiple prices with no end date')) ||
          (typeof errors.prices?.message === 'string' &&
            errors.prices?.message?.includes('multiple prices with no end date'))) && (
          <CustomAlert
            severity='error'
            title='Price Validation Error'
            message='Only one price per currency can have no end date.'
            gridProps={{ xs: 12, sx: { mb: 2 } }}
          />
        )}

      {errors.prices &&
        ((typeof errors.prices.root?.message === 'string' &&
          errors.prices.root.message.includes('Price periods overlap')) ||
          (typeof errors.prices?.message === 'string' &&
            errors.prices?.message?.includes('Price periods overlap'))) && (
          <CustomAlert
            severity='error'
            title='Price Validation Error'
            message='Price periods overlap for one or more currencies. Please ensure price date ranges do not overlap.'
            gridProps={{ xs: 12, sx: { mb: 2 } }}
          />
        )}

      <Box sx={{ mb: 4 }}>
        <SectionTitleTypography>{formatMessage(messages.currencies)}</SectionTitleTypography>
        <Box sx={{ mt: 2 }}>
          <MRTDataGridV2
            rightHeadingComponent={
              !isViewMode ? (
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<AddIcon />}
                  onClick={() => setIsCurrencyModalOpen(true)}
                  disabled={!priceSetMode}
                >
                  {formatMessage(messages.addCurrency)}
                </Button>
              ) : (
                <></>
              )
            }
            data={activePrices}
            columns={currencyColumns}
            enablePagination={false}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableSorting={true}
            enableRowSelection={priceSetMode}
            enableMultiRowSelection={false}
            enableColumnFilterModes={false}
            enableColumnOrdering={false}
            enableColumnDragging={false}
            enableSelectAll={false}
            enableGlobalFilter={false}
            positionActionsColumn='last'
            onRowSelectionChange={(newSelection) => {
              const selectedId = Number(Object.keys(newSelection)[0])

              if (selectedId) {
                setRowSelection({ [selectedId]: true })
              } else {
                setRowSelection({})
              }
            }}
            state={{ rowSelection }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => {
                if (!priceSetMode) return

                setRowSelection((prev) => {
                  const rowId = row.id
                  const isSelected = !!prev[rowId]

                  if (!isSelected) {
                    const currencyData = activePrices.find((price) => price.id === Number(rowId))

                    if (currencyData) {
                      console.log(
                        `Selected currency ${currencyData.currency} (ID: ${currencyData.currencyId})`
                      )
                    }
                  }

                  return {
                    [rowId]: !isSelected,
                  }
                })
              },
              sx: {
                cursor: 'pointer',
                backgroundColor: row.getIsSelected() ? 'rgba(25, 118, 210, 0.12)' : 'inherit',
                '&:hover': {
                  backgroundColor: priceSetMode
                    ? 'inherit'
                    : row.getIsSelected()
                      ? 'rgba(25, 118, 210, 0.18)'
                      : 'rgba(0, 0, 0, 0.04)',
                },
                borderLeft: row.getIsSelected() ? '4px solid #1976d2' : '4px solid transparent',
                transition: 'background-color 0.2s, border-left 0.2s',
              },
            })}
            enableRowActions={false}
            displayColumnDefOptions={{
              'mrt-row-select': {
                size: 0,
                header: '',
                Cell: () => null,
              },
            }}
            getRowId={(row) => String(row.id)}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        {selectedCurrencyId ? (
          <>
            <MRTDataGridV2
              data={filteredPriceHistoryRows}
              columns={priceColumns}
              enableSorting={false}
              leftHeadingComponent={
                <SectionTitleTypography>
                  {formatMessage(messages.priceHistory)}
                </SectionTitleTypography>
              }
              toolbarContent={
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='body2' color='textSecondary'>
                        {formatMessage(messages.updatedAt)}
                      </Typography>
                      <FormSelect
                        options={priceDateOptions}
                        name='priceSet'
                        value={updatedAt}
                        disabled={!priceSetMode}
                        onChange={(point: any) => {
                          const updatedValue = point.target.value
                          console.log('Price set dropdown changed to:', updatedValue)

                          setUpdatedAt(updatedValue)

                          if (selectedCurrencyId && updatedValue) {
                            console.log(
                              'Fetching prices for currency',
                              selectedCurrencyId,
                              'at date',
                              updatedValue
                            )

                            const params = {
                              tenantId,
                              customerId,
                              productId: productId ?? -1,
                              currencyId: selectedCurrencyId,
                              updatedAt: updatedValue,
                            }

                            fetchPrices(params)
                          } else if (!updatedValue) {
                            console.log('Dropdown cleared, using current form prices')
                            setIsPriceModified(false)
                          }
                        }}
                        sx={{ minWidth: 250, pt: 1 }}
                        size='small'
                      />

                      <Typography variant='body2' color='textSecondary'>
                        {formatMessage(messages.updatedBy)}
                      </Typography>
                      <Typography variant='body2'>
                        {priceSets && 'productPrices' in priceSets && priceSets.user
                          ? priceSets.user.firstName + ' ' + priceSets.user.lastName
                          : 'System'}
                      </Typography>
                    </Box>
                    <ConditionalRender condition={!isViewMode}>
                      {priceSetMode ? (
                        <>
                          <Button
                            size='small'
                            onClick={() => handleSetToEditMode()}
                            sx={{ textTransform: 'none', p: 0 }}
                          >
                            {formatMessage(messages.editMode)}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size='small'
                            onClick={() => {
                              setValue('prices', [...originalPrices])
                              setIsPriceModified(false)
                              setPriceSetMode((prev) => !prev)
                            }}
                            sx={{ textTransform: 'none', p: 0 }}
                          >
                            {formatMessage(messages.revertPrice)}
                          </Button>
                          <Button
                            size='small'
                            onClick={() => {
                              setUpdatedAt(undefined)
                              handleAddPriceClick()
                            }}
                            sx={{ textTransform: 'none', p: 0 }}
                          >
                            {formatMessage(messages.addPrice)}
                          </Button>
                        </>
                      )}
                    </ConditionalRender>
                  </Box>
                </Box>
              }
              toolbarContentPosition='full-width'
              componentPadding='16px 0'
              enablePagination={true}
              enableTopToolbar={true}
              positionActionsColumn='last'
              enableEditing={!isViewMode && !priceSetMode}
              editDisplayMode='table'
              onEditingRowSave={({ row }) => {
                const formIndex = row.original.formIndex
                if (formIndex === undefined || formIndex < 0) {
                  console.error('Cannot save row - missing formIndex', row.original)
                  return Promise.reject(new Error('Missing form index'))
                }

                const currentPrices = getValues('prices') || []

                if (formIndex >= currentPrices.length) {
                  console.error('Form index out of bounds', {
                    formIndex,
                    length: currentPrices.length,
                  })
                  return Promise.reject(new Error('Form index out of bounds'))
                }

                setIsPriceModified(true)
                return Promise.resolve()
              }}
              muiTableBodyCellProps={{
                sx: {
                  padding: '8px',
                },
              }}
              state={{
                isLoading: priceDatesLoading,
                showProgressBars: false,
              }}
              initialState={{
                pagination: { pageSize: 20, pageIndex: 0 },
                sorting: [{ id: 'validFrom', desc: true }],
              }}
              muiTableHeadCellProps={{
                sx: {
                  backgroundColor: '#f9f9f9',
                  fontWeight: 'bold',
                },
              }}
              enableRowActions={true}
              renderRowActions={({ row }: any) => {
                if (isViewMode) {
                  return (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title={formatMessage(messages.viewDocuments)}>
                        <IconButton
                          color='primary'
                          onClick={() => handleOpenDocuments(row.original, 'PRICE')}
                          size='small'
                        >
                          <DescriptionIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )
                }

                return (
                  <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <Tooltip title={formatMessage(messages.delete)}>
                      <IconButton
                        color='error'
                        onClick={() => handleDeletePrice(row.original)}
                        size='small'
                        disabled={priceSetMode}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={formatMessage(messages.viewDocuments)}>
                      <IconButton
                        color='primary'
                        onClick={() => handleOpenDocuments(row.original, 'PRICE')}
                        size='small'
                      >
                        <DescriptionIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )
              }}
            />
          </>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
                flexDirection: 'column',
                height: '400px',
              }}
            >
              <Typography variant='h6' sx={{ mb: 1, textAlign: 'center', color: 'text.secondary' }}>
                {formatMessage(messages.pleaseSelectCurrency)}
              </Typography>
            </Box>
          </>
        )}
      </Box>
      {isDocModalOpen && currentDocumentRow && (
        <DocumentModal
          open={isDocModalOpen}
          onClose={() => setIsDocModalOpen(false)}
          rowData={currentDocumentRow}
          documentType={documentType}
          tenantId={tenantId}
          customerId={productData.customerId}
          isViewMode={isViewMode}
          priceIndex={findPriceIndex(currentDocumentRow)}
        />
      )}
      {isCurrencyModalOpen && (
        <CurrencyModal
          open={isCurrencyModalOpen}
          onClose={() => {
            console.log('Closing currency modal')
            setIsCurrencyModalOpen(false)
            setRowSelection({
              [activePrices.length + 1]: true,
            })
          }}
          currencyOptions={currencyOptions}
          onSave={(data: any) => {
            handleAddNewPrice(data)
          }}
          isViewMode={isViewMode}
        />
      )}
      {isPriceModalOpen && (
        <PriceModal
          open={isPriceModalOpen}
          onClose={() => {
            setIsPriceModalOpen(false)
            setSelectedPriceForEdit(null)
          }}
          currencyOptions={currencyOptions}
          initialData={selectedPriceForEdit}
          onSave={handleAddNewPrice}
          selectedCurrencyId={selectedPriceForEdit ? undefined : selectedCurrencyId}
          isViewMode={isViewMode}
          tenantId={tenantId}
          customerId={productData.customerId}
        />
      )}
    </Box>
  )
}

export default PriceDetails
